import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ArrowLeft, Loader2, ShieldAlert, Ban, Smartphone, Trash2, Flag, SlidersHorizontal, Route } from "lucide-react";

const API = "/api";

const TABS = [
  { id: "trusted", label: "Contactos de confianza", icon: ShieldAlert },
  { id: "deletions", label: "Eliminaciones de cuenta", icon: Trash2 },
  { id: "abuse", label: "Reportes de abuso", icon: Flag },
  { id: "devices", label: "Dispositivos / push", icon: Smartphone },
  { id: "filter", label: "Filtro UGC", icon: SlidersHorizontal },
  { id: "routing", label: "Enrutado de perfiles", icon: Route },
];

export default function AdminSafetyCenter() {
  const [tab, setTab] = useState("trusted");
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [trusted, setTrusted] = useState([]);
  const [trustedStatus, setTrustedStatus] = useState("all");
  const [deletions, setDeletions] = useState(null);
  const [deletionStatus, setDeletionStatus] = useState("pending");
  const [abuse, setAbuse] = useState([]);
  const [abuseStatus, setAbuseStatus] = useState("open");
  const [devices, setDevices] = useState([]);
  const [filterCfg, setFilterCfg] = useState(null);
  const [routing, setRouting] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === "trusted") {
        const r = await axios.get(`${API}/admin/trusted-contacts`, { params: { status: trustedStatus } });
        setTrusted(r.data.contacts || []);
      } else if (tab === "deletions") {
        const r = await axios.get(`${API}/admin/account-deletions`, { params: { status: deletionStatus } });
        setDeletions(r.data);
      } else if (tab === "abuse") {
        const r = await axios.get(`${API}/admin/abuse-reports`, { params: { status: abuseStatus } });
        setAbuse(r.data.reports || []);
      } else if (tab === "devices") {
        const r = await axios.get(`${API}/admin/native-devices`);
        setDevices(r.data.devices || []);
      } else if (tab === "filter") {
        const r = await axios.get(`${API}/admin/ugc-filter-config`);
        setFilterCfg(r.data.config);
      } else if (tab === "routing") {
        const r = await axios.get(`${API}/admin/routing/profiles`, { params: { limit: 25 } });
        setRouting(r.data.profiles || []);
      }
    } catch (_e) {
      toast.error("Error al cargar los datos");
    } finally { setLoading(false); }
  }, [tab, trustedStatus, deletionStatus, abuseStatus]);

  useEffect(() => { load(); }, [load]);

  const revokeContact = async (id) => {
    if (!window.confirm("¿Revocar esta relación de confianza?")) return;
    try {
      await axios.post(`${API}/admin/trusted-contacts/${id}/revoke`);
      toast.success("Relación revocada");
      load();
    } catch (_e) { toast.error("No se pudo revocar"); }
  };

  const finalizeDeletion = async (id) => {
    if (!window.confirm("¿Finalizar esta eliminación? Es irreversible.")) return;
    setWorking(true);
    try {
      await axios.post(`${API}/admin/account-deletions/${id}/finalize`);
      toast.success("Eliminación finalizada");
      load();
    } catch (err) {
      toast.error(err?.response?.status === 409
        ? "El periodo de gracia aún no venció"
        : "No se pudo finalizar");
    } finally { setWorking(false); }
  };

  const resolveReport = async (id) => {
    try {
      await axios.post(`${API}/admin/abuse-reports/${id}/resolve`);
      toast.success("Reporte resuelto");
      load();
    } catch (_e) { toast.error("No se pudo resolver"); }
  };

  const revokeDevice = async (id) => {
    if (!window.confirm("¿Revocar este dispositivo?")) return;
    try {
      await axios.post(`${API}/admin/native-devices/${id}/revoke`);
      toast.success("Dispositivo revocado");
      load();
    } catch (_e) { toast.error("No se pudo revocar"); }
  };

  const saveFilter = async () => {
    setWorking(true);
    try {
      const payload = {
        ...filterCfg,
        prohibited_phrases: Array.isArray(filterCfg.prohibited_phrases)
          ? filterCfg.prohibited_phrases
          : String(filterCfg.prohibited_phrases || "").split("\n").filter(Boolean),
        flag_terms: Array.isArray(filterCfg.flag_terms)
          ? filterCfg.flag_terms
          : String(filterCfg.flag_terms || "").split("\n").filter(Boolean),
      };
      const r = await axios.put(`${API}/admin/ugc-filter-config`, payload);
      setFilterCfg(r.data.config);
      toast.success("Configuración del filtro guardada");
    } catch (_e) { toast.error("No se pudo guardar el filtro"); } finally { setWorking(false); }
  };

  return (
    <div className="min-h-screen bg-[#071B2F] pb-16">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <Link to="/admin" className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-6"
              data-testid="admin-safety-back">
          <ArrowLeft className="w-4 h-4" aria-hidden="true" /> Volver al admin
        </Link>

        <h1 className="text-3xl font-bold text-white mb-2">Centro de seguridad y privacidad</h1>
        <p className="text-slate-400 text-sm md:text-base mb-6">
          Contactos de confianza, eliminaciones de cuenta, moderación y dispositivos, todo en el mismo admin.
        </p>

        <div role="tablist" aria-label="Secciones" className="flex flex-wrap gap-2 mb-6">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button key={t.id} role="tab" aria-selected={active} type="button"
                      onClick={() => setTab(t.id)}
                      className={`inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full border transition-colors ${
                        active
                          ? "bg-[#1B84FF]/20 border-emerald-500/50 text-emerald-300"
                          : "bg-[#0B2545] border-white/15 text-slate-300 hover:border-slate-500"}`}
                      data-testid={`admin-safety-tab-${t.id}`}>
                <Icon className="w-4 h-4" aria-hidden="true" /> {t.label}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#5BC0FF]" /></div>
        ) : (
          <div role="tabpanel">
            {tab === "trusted" && (
              <>
                <select value={trustedStatus} onChange={(e) => setTrustedStatus(e.target.value)}
                        aria-label="Filtrar por estado"
                        className="bg-[#0B2545] border border-white/15 text-white rounded-lg px-3 py-2 text-sm mb-4"
                        data-testid="admin-trusted-status-filter">
                  <option value="all">Todos</option>
                  <option value="accepted">Aceptados</option>
                  <option value="invited">Invitados</option>
                  <option value="revoked">Revocados</option>
                </select>
                <div className="overflow-x-auto rounded-xl border border-white/10">
                  <table className="w-full text-sm" data-testid="admin-trusted-table">
                    <caption className="sr-only">Relaciones de confianza</caption>
                    <thead className="bg-[#0B2545] text-slate-400">
                      <tr>
                        <th scope="col" className="text-left p-3">Dueño</th>
                        <th scope="col" className="text-left p-3">Etiqueta pública</th>
                        <th scope="col" className="text-left p-3">Estado</th>
                        <th scope="col" className="text-left p-3">Perfiles</th>
                        <th scope="col" className="text-left p-3">Vinculado</th>
                        <th scope="col" className="text-left p-3">Acción</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trusted.map((c) => (
                        <tr key={c.id} className="border-t border-white/10 text-slate-200"
                            data-testid={`admin-trusted-row-${c.id}`}>
                          <td className="p-3">{c.owner_name || c.owner_user_id?.slice(0, 8)}</td>
                          <td className="p-3">{c.public_label || "—"}</td>
                          <td className="p-3">{c.status}</td>
                          <td className="p-3">{c.profile_count}</td>
                          <td className="p-3">{c.is_linked ? "Sí" : "No"}</td>
                          <td className="p-3">
                            {c.status !== "revoked" && (
                              <button onClick={() => revokeContact(c.id)}
                                      className="inline-flex items-center gap-1 text-red-400 hover:text-red-300 text-xs"
                                      data-testid={`admin-trusted-revoke-${c.id}`}>
                                <Ban className="w-3.5 h-3.5" aria-hidden="true" /> Revocar
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                      {trusted.length === 0 && (
                        <tr><td colSpan={6} className="p-6 text-center text-slate-500"
                                data-testid="admin-trusted-empty">Sin relaciones.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {tab === "deletions" && deletions && (
              <>
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <select value={deletionStatus} onChange={(e) => setDeletionStatus(e.target.value)}
                          aria-label="Filtrar por estado"
                          className="bg-[#0B2545] border border-white/15 text-white rounded-lg px-3 py-2 text-sm"
                          data-testid="admin-deletions-status-filter">
                    <option value="pending">Programadas</option>
                    <option value="completed">Completadas</option>
                    <option value="cancelled">Canceladas</option>
                    <option value="all">Todas</option>
                  </select>
                  <span className="text-xs text-slate-400">
                    Periodo de gracia: {deletions.grace_days} días
                  </span>
                </div>
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-4 text-xs text-amber-200"
                     data-testid="admin-deletions-legal-flags">
                  Colecciones retenidas / revisión legal: {(deletions.legal_review_collections || []).join(", ")}
                </div>
                <div className="overflow-x-auto rounded-xl border border-white/10">
                  <table className="w-full text-sm" data-testid="admin-deletions-table">
                    <caption className="sr-only">Solicitudes de eliminación</caption>
                    <thead className="bg-[#0B2545] text-slate-400">
                      <tr>
                        <th scope="col" className="text-left p-3">Usuario</th>
                        <th scope="col" className="text-left p-3">Estado</th>
                        <th scope="col" className="text-left p-3">Cliente</th>
                        <th scope="col" className="text-left p-3">Solicitado</th>
                        <th scope="col" className="text-left p-3">Programado</th>
                        <th scope="col" className="text-left p-3">Acción</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(deletions.requests || []).map((r) => (
                        <tr key={r.id} className="border-t border-white/10 text-slate-200"
                            data-testid={`admin-deletion-row-${r.id}`}>
                          <td className="p-3 font-mono text-xs">{r.user_id?.slice(0, 10)}</td>
                          <td className="p-3">{r.status}</td>
                          <td className="p-3">{r.client || "—"}</td>
                          <td className="p-3">{(r.requested_at || "").slice(0, 10)}</td>
                          <td className="p-3">{(r.scheduled_for || "").slice(0, 10)}</td>
                          <td className="p-3">
                            {r.status === "scheduled" && (
                              r.grace_elapsed ? (
                                <button onClick={() => finalizeDeletion(r.id)} disabled={working}
                                        className="text-red-400 hover:text-red-300 text-xs"
                                        data-testid={`admin-deletion-finalize-${r.id}`}>
                                  Finalizar
                                </button>
                              ) : (
                                <span className="text-slate-500 text-xs"
                                      data-testid={`admin-deletion-locked-${r.id}`}>En gracia</span>
                              )
                            )}
                          </td>
                        </tr>
                      ))}
                      {(deletions.requests || []).length === 0 && (
                        <tr><td colSpan={6} className="p-6 text-center text-slate-500"
                                data-testid="admin-deletions-empty">
                          No hay solicitudes de eliminación en curso.
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {tab === "abuse" && (
              <>
                <select value={abuseStatus} onChange={(e) => setAbuseStatus(e.target.value)}
                        aria-label="Filtrar por estado"
                        className="bg-[#0B2545] border border-white/15 text-white rounded-lg px-3 py-2 text-sm mb-4"
                        data-testid="admin-abuse-status-filter">
                  <option value="open">Abiertos</option>
                  <option value="resolved">Resueltos</option>
                  <option value="all">Todos</option>
                </select>
                <div className="overflow-x-auto rounded-xl border border-white/10">
                  <table className="w-full text-sm" data-testid="admin-abuse-table">
                    <caption className="sr-only">Reportes de abuso</caption>
                    <thead className="bg-[#0B2545] text-slate-400">
                      <tr>
                        <th scope="col" className="text-left p-3">Origen</th>
                        <th scope="col" className="text-left p-3">Motivo / flags</th>
                        <th scope="col" className="text-left p-3">Conversación</th>
                        <th scope="col" className="text-left p-3">Perfil</th>
                        <th scope="col" className="text-left p-3">Decisión</th>
                        <th scope="col" className="text-left p-3">Estado</th>
                        <th scope="col" className="text-left p-3">Fecha</th>
                        <th scope="col" className="text-left p-3">Acción</th>
                      </tr>
                    </thead>
                    <tbody>
                      {abuse.map((r) => (
                        <tr key={r.id} className="border-t border-white/10 text-slate-200"
                            data-testid={`admin-abuse-row-${r.id}`}>
                          <td className="p-3">{r.source}</td>
                          <td className="p-3 max-w-xs truncate">
                            {r.reason_code || (r.flags || []).join(", ") || r.reason}
                            {r.details ? <span className="block text-xs text-slate-400">{r.details}</span> : null}
                          </td>
                          <td className="p-3 font-mono text-xs" data-testid={`admin-abuse-conv-${r.id}`}>
                            {(r.conversation_id || "—").slice(0, 8)}
                            {r.conversation_blocked && (
                              <span className="ml-2 rounded-full bg-red-500/20 px-2 py-0.5 text-[10px] font-bold text-red-300">
                                Bloqueada
                              </span>
                            )}
                          </td>
                          <td className="p-3 text-xs">
                            {r.protection_type || "—"}
                            {r.session_type ? <span className="block text-slate-400">{r.session_type}</span> : null}
                          </td>
                          <td className="p-3">{r.decision || "—"}</td>
                          <td className="p-3">{r.status || "open"}</td>
                          <td className="p-3">{(r.created_at || "").slice(0, 10)}</td>
                          <td className="p-3">
                            {r.status !== "resolved" && (
                              <button onClick={() => resolveReport(r.id)}
                                      className="text-[#5BC0FF] hover:text-emerald-300 text-xs"
                                      data-testid={`admin-abuse-resolve-${r.id}`}>Resolver</button>
                            )}
                          </td>
                        </tr>
                      ))}
                      {abuse.length === 0 && (
                        <tr><td colSpan={8} className="p-6 text-center text-slate-500"
                                data-testid="admin-abuse-empty">Sin reportes.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {tab === "devices" && (
              <div className="overflow-x-auto rounded-xl border border-white/10">
                <table className="w-full text-sm" data-testid="admin-devices-table">
                  <caption className="sr-only">Dispositivos registrados</caption>
                  <thead className="bg-[#0B2545] text-slate-400">
                    <tr>
                      <th scope="col" className="text-left p-3">Huella</th>
                      <th scope="col" className="text-left p-3">Transporte</th>
                      <th scope="col" className="text-left p-3">Plataforma</th>
                      <th scope="col" className="text-left p-3">Activo</th>
                      <th scope="col" className="text-left p-3">Último éxito</th>
                      <th scope="col" className="text-left p-3">Último fallo</th>
                      <th scope="col" className="text-left p-3">Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {devices.map((d) => (
                      <tr key={d.id} className="border-t border-white/10 text-slate-200"
                          data-testid={`admin-device-row-${d.id}`}>
                        <td className="p-3 font-mono text-xs">{d.device_fingerprint || "—"}</td>
                        <td className="p-3">{d.transport}</td>
                        <td className="p-3">{d.platform || "—"}</td>
                        <td className="p-3">
                          <span className={d.enabled ? "text-[#5BC0FF]" : "text-slate-500"}>
                            {d.enabled ? "Sí" : "No"}
                          </span>
                        </td>
                        <td className="p-3">{(d.last_success_at || "").slice(0, 16)}</td>
                        <td className="p-3">{(d.last_failure_at || "").slice(0, 16)}</td>
                        <td className="p-3">
                          {d.enabled && (
                            <button onClick={() => revokeDevice(d.id)}
                                    className="text-red-400 hover:text-red-300 text-xs"
                                    data-testid={`admin-device-revoke-${d.id}`}>Revocar</button>
                          )}
                        </td>
                      </tr>
                    ))}
                    {devices.length === 0 && (
                      <tr><td colSpan={7} className="p-6 text-center text-slate-500"
                              data-testid="admin-devices-empty">Sin dispositivos.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {tab === "routing" && (
              <div className="overflow-x-auto rounded-xl border border-white/10" data-testid="admin-routing-panel">
                <p className="p-3 text-xs text-slate-400">
                  Grupo de destinatarios por perfil protegido (dueño + contactos de confianza
                  aceptados y asignados). Sin tokens de dispositivo, sin tokens de invitación.
                </p>
                <table className="w-full text-sm" data-testid="admin-routing-table">
                  <caption className="sr-only">Enrutado por perfil protegido</caption>
                  <thead className="bg-[#0B2545] text-slate-400">
                    <tr>
                      <th scope="col" className="text-left p-3">Perfil</th>
                      <th scope="col" className="text-left p-3">Dueño</th>
                      <th scope="col" className="text-left p-3">Confianza</th>
                      <th scope="col" className="text-left p-3">Mensajes</th>
                      <th scope="col" className="text-left p-3">Llamadas</th>
                      <th scope="col" className="text-left p-3">Último enrutado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {routing.map((p) => (
                      <tr key={p.profile_id} className="border-t border-white/10 text-slate-200"
                          data-testid={`admin-routing-row-${p.profile_id}`}>
                        <td className="p-3">
                          {p.profile_label || "—"}
                          <span className="block text-[11px] text-slate-500">{p.protection_type}</span>
                        </td>
                        <td className="p-3">{p.owner?.name || p.owner?.email || "—"}</td>
                        <td className="p-3">
                          {p.trusted_accepted}/{p.trusted_total}
                          <span className="block text-[11px] text-slate-500">
                            {p.trusted.map((c) => c.ref.slice(0, 16)).join(" · ") || "sin contactos"}
                          </span>
                        </td>
                        <td className="p-3" data-testid={`admin-routing-msg-${p.profile_id}`}>
                          {p.message_recipient_count}
                        </td>
                        <td className="p-3" data-testid={`admin-routing-call-${p.profile_id}`}>
                          {p.call_recipient_count}
                        </td>
                        <td className="p-3 text-xs">
                          {p.last_routing_outcome
                            ? `${p.last_routing_outcome.state}${p.last_routing_outcome.answered_by_ref ? ` · ${p.last_routing_outcome.answered_by_ref.slice(0, 16)}` : ""}`
                            : "—"}
                        </td>
                      </tr>
                    ))}
                    {routing.length === 0 && (
                      <tr><td colSpan={6} className="p-6 text-center text-slate-500"
                              data-testid="admin-routing-empty">Sin perfiles.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {tab === "filter" && filterCfg && (
              <section aria-labelledby="filter-heading"
                       className="bg-[#0B2545] border border-white/10 rounded-xl p-5 max-w-2xl"
                       data-testid="admin-filter-config">
                <h2 id="filter-heading" className="text-base md:text-lg font-semibold text-white mb-4">
                  Filtro determinista de contenido
                </h2>
                <p className="text-xs text-slate-400 mb-4">
                  100% local, sin proveedor externo. Las llamadas y las acciones de
                  emergencia nunca se bloquean por moderación de texto.
                </p>
                <label className="flex items-center gap-2 text-sm text-slate-200 mb-4">
                  <input type="checkbox" checked={!!filterCfg.filter_enabled}
                         onChange={(e) => setFilterCfg({ ...filterCfg, filter_enabled: e.target.checked })}
                         data-testid="admin-filter-enabled" /> Filtro activo
                </label>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="prohibited" className="text-slate-300 text-sm">
                      Frases prohibidas (una por línea) — se rechazan
                    </Label>
                    <textarea id="prohibited" rows={4}
                              value={(filterCfg.prohibited_phrases || []).join("\n")}
                              onChange={(e) => setFilterCfg({ ...filterCfg, prohibited_phrases: e.target.value.split("\n") })}
                              className="w-full mt-1 bg-[#14375F] border border-white/15 text-white rounded-lg px-3 py-2 text-sm"
                              data-testid="admin-filter-prohibited" />
                  </div>
                  <div>
                    <Label htmlFor="flagterms" className="text-slate-300 text-sm">
                      Términos a marcar (una por línea) — se permiten y se marcan
                    </Label>
                    <textarea id="flagterms" rows={3}
                              value={(filterCfg.flag_terms || []).join("\n")}
                              onChange={(e) => setFilterCfg({ ...filterCfg, flag_terms: e.target.value.split("\n") })}
                              className="w-full mt-1 bg-[#14375F] border border-white/15 text-white rounded-lg px-3 py-2 text-sm"
                              data-testid="admin-filter-flagterms" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      ["flood_max_messages", "Máx. mensajes por ventana"],
                      ["flood_window_seconds", "Ventana (segundos)"],
                      ["max_repeated_identical", "Máx. repeticiones idénticas"],
                      ["max_links", "Máx. enlaces"],
                      ["max_length", "Longitud máxima"],
                    ].map(([key, label]) => (
                      <div key={key}>
                        <Label htmlFor={`f-${key}`} className="text-slate-300 text-xs">{label}</Label>
                        <Input id={`f-${key}`} type="number" value={filterCfg[key] ?? ""}
                               onChange={(e) => setFilterCfg({ ...filterCfg, [key]: Number(e.target.value) })}
                               className="bg-[#14375F] border-white/15 text-white"
                               data-testid={`admin-filter-${key}`} />
                      </div>
                    ))}
                  </div>
                </div>
                <Button onClick={saveFilter} disabled={working}
                        className="mt-5 bg-[#1B84FF] hover:bg-[#0F6FDB] text-slate-950 font-semibold"
                        data-testid="admin-filter-save">
                  {working ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : "Guardar filtro"}
                </Button>
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
